import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { IModalComponent } from '../../models/modal-behaviour.interface';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { InputFieldType } from '../../models/input-types.enum';
import { ThirdPartiesApiService } from 'src/app/core/services/api/third-parties-api.service';
import { finalize, take } from 'rxjs';
import { ApiResponse } from '../../models/api-response.interface';
import { NotificationsService } from 'angular2-notifications';
import { ErrorHandlingService } from 'src/app/core/services/error-handling.service';

@Component({
  selector: 'app-third-party-validation-modal',
  templateUrl: './third-party-validation-modal.component.html',
  styleUrls: ['./third-party-validation-modal.component.scss'],
})
export class ThirdPartyValidationModalComponent extends IModalComponent implements OnInit {
  @Output() onUpdated = new EventEmitter<any>();
  @Input() thirdParty?: any;
  @Input() thirdPartyType?: string; // 'repair_facility' or 'service_advisor'
  
  validationForm!: FormGroup;
  isLoading: boolean = false;
  missingFields: string[] = [];
  protected readonly InputFieldType = InputFieldType;

  constructor(
    private fb: FormBuilder,
    private thirdPartiesService: ThirdPartiesApiService,
    private notificationService: NotificationsService,
    private errorHandler: ErrorHandlingService,
  ) {
    super();
  }

  get formAddress(): FormGroup {
    return this.validationForm.get('formAddress') as FormGroup;
  }

  ngOnInit(): void {
    this.initForm();
  }

  /**
   * Validates if a third party has all required contact information
   * @param thirdParty - The third party object to validate
   * @param thirdPartyType - Type of third party ('repair_facility' or 'service_advisor')
   * @returns Array of missing field names, empty if all required fields are present
   */
  static validateThirdParty(thirdParty: any, thirdPartyType?: string): string[] {
    const missing: string[] = [];
    const contactDetails = thirdParty?.contact_details;

    // Required fields validation
    if (!contactDetails?.phone && !contactDetails?.mobile) {
      missing.push('Phone');
    }

    // Email is required for both types
    if (!contactDetails?.email) {
      missing.push('Email');
    }

    // Address is no longer required for any type

    return missing;
  }

  /**
   * Opens the modal with the third party to validate
   */
  openForValidation(thirdParty: any, thirdPartyType: string): void {
    this.thirdParty = thirdParty;
    this.thirdPartyType = thirdPartyType;
    this.missingFields = ThirdPartyValidationModalComponent.validateThirdParty(thirdParty, thirdPartyType);
    this.prefillForm();
    this.toggleModal();
  }

  private prefillForm(): void {
    if (!this.thirdParty) return;

    const contactDetails = this.thirdParty.contact_details;
    const addressDetails = contactDetails?.address_details;

    // Pre-fill company name for both types
    const companyName = this.thirdParty.company_name || this.thirdParty.name || '';

    // Pre-fill contact information
    this.validationForm.patchValue({
      name: companyName,
      first_name: contactDetails?.first_name || '',
      last_name: contactDetails?.last_name || '',
      mobile: contactDetails?.phone?.replace(/^\+1/, '') || contactDetails?.mobile?.replace(/^\+1/, '') || '',
      email: contactDetails?.email || '',
    });
  }

  override handleModalHide(): void {
    this.validationForm.reset();
    this.thirdParty = undefined;
    this.missingFields = [];
  }

  handleFormSubmit(): void {
    if (!this.validationForm.valid) {
      this.notificationService.error(
        'Error',
        'Please ensure all required fields are filled correctly',
      );
      this.validationForm.markAllAsTouched();
      return;
    }

    const formValue = { ...this.validationForm.value };
    const address = {
      ...formValue?.formAddress,
      address: formValue?.formAddress?.street_address?.label,
    };
    delete formValue.formAddress;
    delete formValue.street_address;

    const updatedThirdParty = {
      ...formValue,
      ...address,
      mobile: formValue?.mobile ? `+1${formValue.mobile.replace(/\D/g, '')}` : undefined,
      type_id: this.thirdParty?.type_id || (this.thirdPartyType === 'repair_facility' ? 6 : 7),
      name: formValue?.name, // Always include company name
    };

    // Remove formAddress from the payload
    delete updatedThirdParty.formAddress;

    this.isLoading = true;
    this.thirdPartiesService
      .update(this.thirdParty.id, updatedThirdParty)
      .pipe(
        take(1),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: (result: ApiResponse) => {
          this.notificationService.success('Success', result.message || 'Third party updated successfully');
          
          // Merge updated data with original third party
          const updatedData = {
            ...this.thirdParty,
            contact_details: {
              ...this.thirdParty.contact_details,
              first_name: updatedThirdParty.first_name,
              last_name: updatedThirdParty.last_name,
              phone: updatedThirdParty.mobile,
              email: updatedThirdParty.email,
              address_details: {
                ...this.thirdParty.contact_details?.address_details,
                address: address.address,
                zip_code: updatedThirdParty.zip_code,
                city: updatedThirdParty.city,
                state: updatedThirdParty.state,
                country: updatedThirdParty.country,
              }
            }
          };

          if (this.thirdPartyType === 'repair_facility') {
            updatedData.company_name = updatedThirdParty.name;
            updatedData.name = updatedThirdParty.name;
          }

          this.onUpdated.emit(updatedData);
          this.toggleModal();
        },
        error: (error: { error: Error }) =>
          this.errorHandler.errorHandling(error),
      });
  }

  private initForm(): void {
    if (this.thirdPartyType === 'repair_facility') {
      this.validationForm = this.fb.group({
        name: [null, Validators.required],
        first_name: [null],
        last_name: [null],
        mobile: [null, Validators.required],
        email: [null, [Validators.required, Validators.email]],
        formAddress: this.fb.group({
          street_address: [null],
          country: [null],
          state: [null],
          city: [null],
          zip_code: [null],
        }),
      });
    } else {
      // service_advisor
      this.validationForm = this.fb.group({
        name: [null, Validators.required],
        first_name: [null, Validators.required],
        last_name: [null, Validators.required],
        mobile: [null, Validators.required],
        email: [null, [Validators.required, Validators.email]],
        formAddress: this.fb.group({
          street_address: [null],
          country: [null],
          state: [null],
          city: [null],
          zip_code: [null],
        }),
      });
    }
  }
}
